import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import ProductInfo from "./ProductInfo";
import { getAllProducts } from "../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../services/indexMediaBaseUrl";
import { Grid, Card, CardActionArea, CardMedia, Typography, CircularProgress, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import StarIcon from "@mui/icons-material/Star";

export default function ProductInfoPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation("ProductPageInf");
  const [related, setRelated] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  // determine language: prefer i18n.language, fallback to localStorage 'lang'
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";
  // keep document lang/dir in sync (safe-guard)
  useEffect(() => {
    try {
      document.documentElement.lang = lang;
      document.documentElement.dir = isAr ? "rtl" : "ltr";
    } catch {}
  }, [lang, isAr]);
  const getName = (p) => (isAr ? (p?.name_ar || p?.name_en) : (p?.name_en || p?.name_ar));
  useEffect(() => {
    document.title = 'DIDA MAX — Product Details';
  }, []);
  // hooks must run unconditionally
  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetchRelated = async () => {
      setLoadingRelated(true);
      try {
        const res = await getAllProducts();
        const list = res?.products || res?.data?.products || res?.data || [];
        // flatten products array
        const all = [];
        (Array.isArray(list) ? list : []).forEach((entry) => {
          const products = Array.isArray(entry.products) ? entry.products : [];
          products.forEach((p) => {
            // ensure product carries categroy if entry has category
            const normalized = { ...p, categroy: p.categroy || entry.category || p.category || {} };
            all.push(normalized);
          });
        });

        // find current product to get its category id
        const current = all.find((p) => String(p.id) === String(id));
        const catId = current?.categroy?.id || current?.category_id || null;

        if (catId) {
          const relatedList = all.filter((p) => {
            const pCat = p.categroy?.id || p.category_id || null;
            return String(pCat) === String(catId) && String(p.id) !== String(id);
          });
          if (mounted) setRelated(relatedList);
        } else {
          if (mounted) setRelated([]);
        }
      } catch (err) {
        if (mounted) setRelated([]);
      } finally {
        if (mounted) setLoadingRelated(false);
      }
    };
    fetchRelated();
    return () => {
      mounted = false;
    };
  }, [id, t, i18n?.language]);

  // now safe to return early if no id
  if (!id) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          {t("product_id_missing", { defaultValue: "معرف المنتج غير محدد." })}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* Main Product Info */}
      <ProductInfo productId={id} />

      {/* Related Products Section */}
      {(related.length > 0 || loadingRelated) && (
        <Box
          sx={{
            bgcolor: "grey.50",
            py: { xs: 4, md: 6 },
            mt: 4,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                mb: 4,
                pb: 3,
                borderBottom: "2px solid",
                borderImage: "linear-gradient(90deg, #d5aa2f, #e6c76a, transparent) 1",
              }}
            >
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                }}
              >
                {t("related_products", { defaultValue: "منتجات مشابهة" })}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {t("related_products_desc", { defaultValue: "منتجات من نفس الفئة قد تعجبك" })}
              </Typography>
            </Box>

            {loadingRelated ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 8,
                }}
              >
                <CircularProgress size={56} sx={{ color: "primary.main" }} />
              </Box>
            ) : related && related.length > 0 ? (
              <Grid container spacing={3}>
                {related.slice(0, 8).map((p, index) => {
                  const name = getName(p) || "";
                  const img = getImageUrl(
                    p.main_photo || (p.images && p.images[0] && p.images[0].image) || ""
                  );
                  const currency = p.currencies?.[0] || {};
                  const rawPrice = currency.price ?? p.price ?? 0;
                  const rawDiscount = currency.price_after_discount ?? p.price_after_discount ?? 0;
                  const displayPrice = parseFloat(rawPrice) || 0;
                  const displayDiscount = parseFloat(rawDiscount) || 0;
                  const hasDiscount = displayDiscount > 0 && displayDiscount < displayPrice;
                  const rating = p.reviews_summary?.average_rating;
                  const reviewCount = p.reviews_summary?.total_reviews;

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 32px rgba(213, 170, 47, 0.15)",
                            "& .related-image": {
                              transform: "scale(1.1)",
                            },
                          },
                          animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
                          "@keyframes fadeInUp": {
                            from: {
                              opacity: 0,
                              transform: "translateY(20px)",
                            },
                            to: {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                          },
                        }}
                      >
                        <CardActionArea
                          component={Link}
                          to={`/product/${p.id}`}
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "stretch",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              bgcolor: "#fafafa",
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={img || "/placeholder.png"}
                              alt={name}
                              className="related-image"
                              sx={{
                                height: 220,
                                objectFit: "cover",
                                transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                            />
                            
                            {hasDiscount && (
                              <Chip
                                label={`-${Math.round((1 - displayDiscount / displayPrice) * 100)}%`}
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 12,
                                  right: 12,
                                  bgcolor: "error.main",
                                  color: "#fff",
                                  fontWeight: 800,
                                  fontSize: "0.75rem",
                                  height: 28,
                                }}
                              />
                            )}

                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)",
                                pointerEvents: "none",
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              p: 2,
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              sx={{
                                mb: 1,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: 1.4,
                                minHeight: "2.8em",
                              }}
                            >
                              {name}
                            </Typography>

                            {/* Price */}
                            {displayPrice > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                {hasDiscount ? (
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        textDecoration: "line-through",
                                        color: "text.disabled",
                                        fontWeight: 600,
                                        display: "block",
                                      }}
                                    >
                                      {displayPrice} {isAr ? "ج.م" : "EGP"}
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{ color: "primary.main", fontWeight: 800 }}
                                    >
                                      {displayDiscount} {isAr ? "ج.م" : "EGP"}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="h6"
                                    sx={{ color: "primary.main", fontWeight: 800 }}
                                  >
                                    {displayPrice} {isAr ? "ج.م" : "EGP"}
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* Rating */}
                            {rating && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  mt: "auto",
                                }}
                              >
                                <StarIcon
                                  sx={{ fontSize: 16, color: "primary.main" }}
                                />
                                <Typography
                                  variant="body2"
                                  fontWeight={700}
                                  color="primary.dark"
                                >
                                  {rating}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({reviewCount})
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="h6" color="text.secondary" fontWeight={600}>
                  {t("no_related_products", { defaultValue: "لا توجد منتجات مشابهة" })}
                </Typography>
              </Paper>
            )}
          </Container>
        </Box>
      )}
    </Box>
  );
}
